import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useUserRoles } from "@/hooks/useUserRoles";

export type AudienceRole = "entrepreneur" | "mentor" | "admin" | "instructor";

export interface CurriculumAudience {
  curriculum_id: string;
  audience_role: AudienceRole;
}

// Fetch all curriculum audiences
export function useCurriculumAudiences() {
  return useQuery({
    queryKey: ["curriculum-audiences"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("curriculum_audiences")
        .select("curriculum_id, audience_role");

      if (error) throw error;

      // Build a map of curriculum_id -> audience roles
      const audienceMap = new Map<string, AudienceRole[]>();
      (data || []).forEach((a) => {
        const existing = audienceMap.get(a.curriculum_id) || [];
        audienceMap.set(a.curriculum_id, [...existing, a.audience_role as AudienceRole]);
      });

      return audienceMap;
    },
  });
}

// Check if the current user can access a specific curriculum
export function useCanAccessCurriculum(curriculumId: string) {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { data: userRoles } = useUserRoles();
  const { data: audienceMap } = useCurriculumAudiences();

  const canAccess = (() => {
    if (!curriculumId) return false;
    
    const audiences = audienceMap?.get(curriculumId);
    
    // If no audiences defined, curriculum is NOT eligible (safer default)
    // But admins/instructors can still access for management
    if (!audiences || audiences.length === 0) {
      return userRoles?.includes("admin") || userRoles?.includes("instructor") || false;
    }

    // Check if user's profile role matches
    if (profile?.role && audiences.includes(profile.role as AudienceRole)) {
      return true;
    }

    // Check if user has admin or instructor role in user_roles
    if (userRoles?.includes("admin") && audiences.includes("admin")) {
      return true;
    }
    if (userRoles?.includes("instructor") && audiences.includes("instructor")) {
      return true;
    }

    return false;
  })();

  return {
    canAccess,
    isLoading: !audienceMap || (user && !profile),
  };
}

// Get eligibility label for display
export function getEligibilityLabel(audiences: AudienceRole[] | undefined): string {
  if (!audiences || audiences.length === 0) {
    return "Restricted";
  }

  const hasEntrepreneur = audiences.includes("entrepreneur");
  const hasMentor = audiences.includes("mentor");
  const hasAdmin = audiences.includes("admin");
  const hasInstructor = audiences.includes("instructor");

  // If all main roles are included, it's for everyone
  if (hasEntrepreneur && hasMentor) {
    return "Available to Everyone";
  }

  const labels: string[] = [];
  if (hasEntrepreneur) labels.push("Entrepreneurs");
  if (hasMentor) labels.push("Mentors");
  if (hasAdmin) labels.push("Admins");
  if (hasInstructor) labels.push("Instructors");

  if (labels.length === 0) {
    return "Restricted";
  }

  return `Available to ${labels.join(" & ")}`;
}

// Get eligibility badge variant
export function getEligibilityVariant(audiences: AudienceRole[] | undefined): "default" | "secondary" | "destructive" | "outline" {
  if (!audiences || audiences.length === 0) {
    return "destructive";
  }
  
  const hasEntrepreneur = audiences.includes("entrepreneur");
  const hasMentor = audiences.includes("mentor");
  
  if (hasEntrepreneur && hasMentor) {
    return "default"; // Green for "everyone"
  }
  
  return "secondary";
}

// Check eligibility based on user's roles vs curriculum audiences
export function checkUserEligibility(
  profileRole: string | null | undefined,
  userRoles: string[] | undefined,
  audiences: AudienceRole[] | undefined
): boolean {
  // If no audiences defined, not eligible (except admins/instructors)
  if (!audiences || audiences.length === 0) {
    return userRoles?.includes("admin") || userRoles?.includes("instructor") || false;
  }

  // Check profile role (entrepreneur, mentor)
  if (profileRole && audiences.includes(profileRole as AudienceRole)) {
    return true;
  }

  // Check user_roles (admin, instructor)
  if (userRoles?.includes("admin") && audiences.includes("admin")) {
    return true;
  }
  if (userRoles?.includes("instructor") && audiences.includes("instructor")) {
    return true;
  }

  return false;
}
